import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import SpeechItem from './SpeechItem';
import HashtagDisplay from './HashtagDisplay';
import SpeechSummaryPopup from './SpeechSummaryPopup';
import PoliticalLeaningBadge from './PoliticalLeaningBadge';
import { generateSpeechSummary } from '../claudeApi';
import PasswordProtection from './PasswordProtection';

const CollapsibleAgendaItem = ({ agendaItem, onHashtagClick }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [summaryPopupOpen, setSummaryPopupOpen] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summary, setSummary] = useState('');
  const [currentSpeeches, setCurrentSpeeches] = useState([]);
  const navigate = useNavigate();

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const handleViewAllSpeeches = (e) => {
    e.stopPropagation(); // Prevent the collapse/expand from triggering
    navigate(`/speeches/${agendaItem.agendaItemInfo.id}`);
  };

  const handleSummaryClick = async (e) => {
    if (e && e.stopPropagation) {
      e.stopPropagation(); // Prevent the collapse/expand from triggering
    }
    setSummaryPopupOpen(true);
    setSummary('');
    
    try {
      // Fetch all speeches for this agenda item (not just the speaker's speeches)
      const { data, error } = await supabase
        .from('speeches')
        .select('*')
        .eq('agenda_item_id', agendaItem.agendaItemInfo.id)
        .order('time', { ascending: true });
        
      if (error) throw error;
      setCurrentSpeeches(data || []);
    } catch (err) {
      console.error('Error fetching speeches:', err);
      setSummary(`Error fetching speeches: ${err.message}`);
    }
  };

  // Wrapper function for password protection that doesn't require an event
  const handleSummaryClickProtected = () => {
    handleSummaryClick();
  };

  const handleGenerateSummary = async (customPrompt) => {
    setSummaryLoading(true);
    
    try {
      // Generate summary using Claude API with custom prompt
      const summaryText = await generateSpeechSummary(currentSpeeches, customPrompt);
      setSummary(summaryText);
    } catch (err) {
      console.error('Error generating summary:', err);
      setSummary(`Error generating summary: ${err.message}`);
    } finally {
      setSummaryLoading(false);
    }
  };

  return (
    <>
      <div className="mb-4 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        {/* Clickable Header */}
        <div 
          className="p-4 bg-gray-50 dark:bg-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-200"
          onClick={toggleExpanded}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0 pr-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white m-0 mb-2">
                {agendaItem.agendaItemInfo.itemNumber}. {agendaItem.agendaItemInfo.title}
              </h3>
              <div className="mb-2">
                <PoliticalLeaningBadge leaningScore={agendaItem.agendaItemInfo.leaning_score} />
              </div>
              {agendaItem.agendaItemInfo.description && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                  {agendaItem.agendaItemInfo.description}
                </p>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              {/* View All Speeches Button */}
              <button
                onClick={handleViewAllSpeeches}
                className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-md text-primary bg-primary/10 hover:bg-primary/20 dark:text-primary dark:bg-primary/20 dark:hover:bg-primary/30 transition-colors"
              >
                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
                View All
              </button>
              
              {/* Summary Button */}
              <PasswordProtection onAuthenticated={handleSummaryClickProtected}>
                <button
                  className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-md text-secondary bg-secondary/10 hover:bg-secondary/20 dark:text-secondary dark:bg-secondary/20 dark:hover:bg-secondary/30 transition-colors"
                >
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                  </svg>
                  Summary
                </button>
              </PasswordProtection>
              
              {/* Chevron Icon */}
              <svg 
                className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
          <HashtagDisplay 
            hashtags={agendaItem.agendaItemInfo.hashtags} 
            className="mt-2" 
            onHashtagClick={onHashtagClick}
          />
        </div>
        
        {/* Collapsible Content */}
        <div 
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            isExpanded ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="p-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {agendaItem.speeches.map(speech => (
                <SpeechItem key={speech.id} speech={speech} />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Summary Popup */}
      <SpeechSummaryPopup
        isOpen={summaryPopupOpen}
        onClose={() => setSummaryPopupOpen(false)}
        summary={summary}
        loading={summaryLoading}
        onGenerateSummary={handleGenerateSummary}
      />
    </>
  );
};

export default CollapsibleAgendaItem;
